import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';

import {
  calendarApi,
  EVENT_TYPE_KEYS,
  type CalendarEvent,
  type CalendarEventType,
} from '@/api/calendar';
import { attendanceApi } from '@/api/attendance';
import { useAuthStore } from '@/stores/auth';
import { invalidateStudentCalendar, useCalendarItems } from '@/hooks/useCalendarItems';
import { useIsMobile } from '@/hooks/useIsMobile';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { calendarInternalPath, showAttendanceCode } from '@/lib/calendarAccess';
import { externalHttpUrl, openExternal } from '@/lib/externalLink';
import { clearSchedulePlace, peekSchedulePlace, rememberSchedulePlace } from '@/lib/scheduleReturn';
import {
  MS_DAY,
  deadlineNear,
  itemsOn,
  sameDay,
  startOfDay,
  startOfWeek,
  type CalItem,
} from '@/lib/calendarItems';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { ScheduleSidebar } from './desktop/ScheduleSidebar';
import { TrainerScheduleStrip } from '@/components/schedule/TrainerScheduleStrip';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { humanizeError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import i18nInstance from '@/app/i18n';

// Date/time formatting must follow the selected UI language, not a hardcoded
// Russian locale — otherwise EN/KZ users see mixed-language month names.
const dateLocale = () => {
  const l = i18nInstance.language ?? '';
  return l.startsWith('en') ? 'en-US' : l.startsWith('kk') || l.startsWith('kz') ? 'kk' : 'ru-RU';
};

// Emoji per calendar item kind — live lessons stand out with a 🔴 "live" marker
// so a broadcast class reads at a glance among platform events and notes.
function kindEmoji(kind: CalItem['kind']): string {
  switch (kind) {
    case 'live_lesson':
      return '🔴';
    case 'speaking_room':
      return '🎙️';
    case 'personal':
      return '📌';
    default:
      return '📢';
  }
}

type ViewMode = 'month' | 'week' | 'day';

// Список событий (живые уроки, комнаты, события платформы, личные записи) собирает
// `lib/calendarItems.ts` — один на эту страницу и на недельные виджеты главной.

/**
 * Student schedule — internal calendar (not Google Calendar, TZ §5.8) with
 * month / week / day views and colour marking: Live lessons (blue), Speaking
 * Rooms (green), platform events (orange) and personal notes (grey).
 *
 * The page also hosts the "Submit attendance code" entrypoint (TZ §5.9).
 */
export function StudentSchedulePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const user = useAuthStore((st) => st.user);
  // Выбранный день недели на широком экране: список под сеткой показывает
  // именно его, а не всегда сегодняшний.
  /**
   * ⚠️ ВОЗВРАТ ИЗ КОМНАТЫ И С ЖИВОГО УРОКА — НА ТОТ ЖЕ ДЕНЬ И НЕДЕЛЮ (02.10.2026). В приложении в комнату входят
   * только отсюда, а «назад» из неё ведёт сюда же; страница при уходе размонтируется, и без памяти ученик,
   * вошедший из четверга следующей недели, возвращался бы на сегодня. Перед «Войти» место запоминается
   * (`leaveForCall`), при открытии — читается один раз и стирается (`lib/scheduleReturn.ts`).
   */
  const [back] = useState(() => peekSchedulePlace());
  useEffect(() => {
    clearSchedulePlace();
  }, []);
  const [selectedDay, setSelectedDay] = useState<Date | null>(back?.selectedDay ?? null);
  /**
   * ⚠️ НА ТЕЛЕФОНЕ ОТКРЫВАЕТСЯ НЕДЕЛЯ, А НЕ МЕСЯЦ (07.09.2026). Месяц на узком
   * экране — сетка из сорока двух клеток, куда не влезает ничего, кроме числа:
   * занятий не видно, и календарь превращается в отрывной листок. Заказчик:
   * «почему у нас на телефоне уродский календарь, давай сделаем как на компе».
   * На компьютере открыта именно неделя.
   */
  const [view, setView] = useState<ViewMode>(back?.view ?? 'week');
  const [cursor, setCursor] = useState<Date>(back?.cursor ?? startOfDay(new Date()));
  const leaveForCall = () => rememberSchedulePlace({ cursor, selectedDay, view });
  const [codeOpen, setCodeOpen] = useState(false);
  const [personalOpen, setPersonalOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [selected, setSelected] = useState<CalItem | null>(null);

  // Запросы и сборка списка — общие с недельными виджетами главной (`hooks/useCalendarItems.ts`).
  // Живые уроки и комнаты — по разделу и выключателю (`lib/calendarAccess.ts`): нет раздела — нет ни события,
  // ни кнопки «Войти», ни запроса.
  const { items, isLoading, isError, error, showLive, showRooms } = useCalendarItems();
  // Код посещения вводят на живом уроке: без уроков кнопка вела бы в отказ сервера.
  const canCode = showAttendanceCode(user);

  const deletePersonal = useMutation({
    mutationFn: (id: string) => calendarApi.deletePersonal(id),
    onSuccess: () => {
      void invalidateStudentCalendar(qc);
      setSelected(null);
    },
  });

  const shift = (dir: -1 | 1) => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCursor(startOfDay(d));
  };

  const periodLabel =
    view === 'month'
      ? cursor.toLocaleDateString(dateLocale(), { month: 'long', year: 'numeric' })
      : view === 'week'
        ? `${startOfWeek(cursor).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' })} – ${new Date(startOfWeek(cursor).getTime() + 6 * MS_DAY).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' })}`
        : cursor.toLocaleDateString(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' });

  // ── Компьютер: раскладка по макету «Desktop Web v2» ─────────────────────
  //
  // ⚠️ ТЕЛЕФОН ОСТАЁТСЯ НА ПРЕЖНЕЙ РАЗМЕТКЕ, она ниже: у страницы была ОДНА
  // разметка на оба размера, и переделка задела бы приложение.
  if (!isMobile) {
    const weekStartD = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, i) => new Date(weekStartD.getTime() + i * MS_DAY));
    const eventsOn = (d: Date) => items.filter((x) => sameDay(x.start, d));
    const now = new Date();
    const upcoming = items.find((x) => x.start >= now);
    const shown = selectedDay ?? now;
    const shownList = eventsOn(shown);
    const weekItems = items.filter((x) => x.start >= weekStartD && x.start < new Date(weekStartD.getTime() + 7 * MS_DAY));
    const past = weekItems.filter((x) => x.start < now).length;

    /**
     * ⚠️ Подпись и стрелки — СВОИ, недельные.
     *
     * Общие `periodLabel` и `shift` считаются по режиму `view`, а он на широком
     * экране не показывается вовсе: экран всегда недельный. Из-за этого над
     * сеткой из семи дней стояло «Сентябрь 2026», а стрелка листала месяц.
     */
    const weekEndD = new Date(weekStartD.getTime() + 6 * MS_DAY);
    const weekLabel = `${weekStartD.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' })} — ${weekEndD.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' })}`;
    const shiftWeek = (dir: -1 | 1) => {
      const d = new Date(cursor);
      d.setDate(d.getDate() + dir * 7);
      setCursor(startOfDay(d));
      setSelectedDay(null);
    };

    return (
      <div>
        <StudentTopBar
          title={t('schedule.title')}
          name={user?.full_name}
          avatarUrl={user?.avatar_url}
        />

        {/* Когда куратор открыл тренажёры. Полосы нет, пока расписания нет. */}
        <TrainerScheduleStrip className="mb-4" />

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            {/* ── Ближайшее занятие ───────────────────────────────────── */}
            {upcoming ? (
              <div className="flex items-center gap-[18px] rounded-[24px] bg-primary px-6 py-5">
                <div className="grid h-[54px] w-[54px] flex-none place-items-center rounded-2xl bg-on-primary/20 text-center">
                  <div>
                    <div className="text-[16px] font-bold leading-none text-on-primary">{upcoming.start.getDate()}</div>
                    <div className="text-[9px] font-semibold uppercase tracking-[0.06em] text-on-primary/85">
                      {upcoming.start.toLocaleDateString(dateLocale(), { weekday: 'short' }).replace('.', '')}
                    </div>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-on-primary/85">
                    {t('schedule.nextClass')}
                  </div>
                  <div className="mt-[7px] truncate text-[20px] font-bold leading-[1.2] text-on-primary">{upcoming.title}</div>
                  <div className="mt-1 text-[12.5px] text-on-primary/85">
                    {upcoming.start.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' })}
                    {upcoming.durationMin ? ` · ${t('speakingRooms.minutes', { count: upcoming.durationMin })}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(upcoming)}
                  className="flex-none rounded-full bg-white px-[22px] py-[13px] text-[14.5px] font-bold text-primary-pressed transition hover:bg-primary-container"
                >
                  {t('schedule.open')}
                </button>
              </div>
            ) : (
              <div className="rounded-[24px] bg-surface-container px-6 py-5 text-[13px] text-on-surface-variant">
                {t('schedule.noUpcoming')}
              </div>
            )}

            {/* ── Неделя ──────────────────────────────────────────────── */}
            <div className="mx-0.5 mb-3 mt-[22px] flex items-center justify-between">
              <h2 className="font-display text-[15.5px] font-bold text-on-surface">{weekLabel}</h2>
              <div className="flex flex-none items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => shiftWeek(-1)}
                  aria-label={t('schedule.prev')}
                  className="grid h-[30px] w-[30px] place-items-center rounded-full bg-surface-container transition hover:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-[14px] text-on-surface-variant">chevron_left</span>
                </button>
                <button
                  type="button"
                  onClick={() => shiftWeek(1)}
                  aria-label={t('schedule.next')}
                  className="grid h-[30px] w-[30px] place-items-center rounded-full bg-surface-container transition hover:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-[14px] text-on-surface-variant">chevron_right</span>
                </button>
                {/* Код посещения и своё событие — рядом с переключением недели,
                    чтобы шапка осталась такой же, как на других экранах. */}
                {canCode && (
                  <button
                    type="button"
                    onClick={() => setCodeOpen(true)}
                    aria-label={t('schedule.attendanceCode')}
                    className="grid h-[30px] w-[30px] place-items-center rounded-full bg-surface-container transition hover:bg-surface-container-high"
                  >
                    <span className="material-symbols-outlined text-[14px] text-on-surface-variant">vpn_key</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setEditing(null); setPersonalOpen(true); }}
                  aria-label={t('schedule.personalEvent')}
                  className="grid h-[30px] w-[30px] place-items-center rounded-full bg-primary transition hover:bg-primary-hover"
                >
                  <span className="material-symbols-outlined text-[14px] text-on-primary">add</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2">
              {days.map((d) => {
                const isToday = sameDay(d, now);
                const isShown = sameDay(d, shown);
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => setSelectedDay(d)}
                    className={cn(
                      'min-h-[150px] rounded-[18px] px-2 pb-3.5 pt-3 text-left transition',
                      isShown ? 'bg-primary-container' : 'bg-surface-container hover:bg-surface-container-high',
                    )}
                  >
                    <div
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-[0.06em]',
                        isShown ? 'text-on-primary-container' : 'text-on-surface-variant',
                      )}
                    >
                      {d.toLocaleDateString(dateLocale(), { weekday: 'short' }).replace('.', '')}
                    </div>
                    <div
                      className={cn(
                        'mt-[3px] text-[19px] font-bold',
                        isToday ? 'text-primary' : isShown ? 'text-on-primary-container' : 'text-on-surface',
                      )}
                    >
                      {d.getDate()}
                    </div>
                    <div className="mt-[11px] flex flex-col gap-[5px]">
                      {eventsOn(d).slice(0, 3).map((e) => (
                        <span key={e.id} className="rounded-[9px] bg-surface-container-lowest px-[7px] py-1.5">
                          <span className="block text-[9.5px] font-bold text-on-surface-variant">
                            {e.start.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="mt-0.5 block line-clamp-2 text-[10px] font-semibold leading-[1.3] text-on-surface">
                            {e.title}
                          </span>
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ── События выбранного дня ──────────────────────────────── */}
            <DayList day={shown} items={shownList} onSelect={setSelected} />
          </div>

          <ScheduleSidebar weekTotal={weekItems.length} past={past} />
        </div>

        {selected && (
          <EventDetailsModal
            item={selected}
            showLive={showLive}
            showRooms={showRooms}
            onJoin={leaveForCall}
            onClose={() => setSelected(null)}
            onEdit={() => {
              if (selected.personal) {
                setEditing(selected.personal);
                setPersonalOpen(true);
                setSelected(null);
              }
            }}
            onDelete={() => selected.personal && deletePersonal.mutate(selected.personal.id)}
            deleting={deletePersonal.isPending}
            deleteError={deletePersonal.isError ? humanizeError(deletePersonal.error) : null}
          />
        )}
        {personalOpen && (
          <PersonalEventModal
            existing={editing}
            onClose={() => { setPersonalOpen(false); setEditing(null); }}
            onSaved={() => {
              void invalidateStudentCalendar(qc);
              setPersonalOpen(false);
              setEditing(null);
            }}
          />
        )}
        <AttendanceCodeModal open={codeOpen} onClose={() => setCodeOpen(false)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-display">{t('schedule.title')}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canCode && (
            <button
              onClick={() => setCodeOpen(true)}
              title={t('schedule.attendanceCode')}
              aria-label={t('schedule.attendanceCode')}
              className="grid h-11 w-11 place-items-center rounded-button bg-surface-container-lowest text-on-surface-variant shadow-soft transition hover:bg-surface-container-low"
            >
              <span className="material-symbols-outlined">vpn_key</span>
            </button>
          )}
          <button
            onClick={() => { setEditing(null); setPersonalOpen(true); }}
            title={t('schedule.personalEvent')}
            aria-label={t('schedule.personalEvent')}
            className="grid h-11 w-11 place-items-center rounded-button bg-gradient-to-br from-primary to-primary-pressed text-on-primary shadow-soft transition hover:brightness-105"
          >
            <span className="material-symbols-outlined">add</span>
          </button>
        </div>
      </header>

      {/* Расписание тренажёров — тот же блок, что на широком экране. */}
      <TrainerScheduleStrip />

      {/* View toggle — full-width segmented, gradient active */}
      <div className="flex gap-1 rounded-button bg-surface-container-low p-1">
        {(['day', 'week', 'month'] as ViewMode[]).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={cn(
              'flex-1 rounded-button px-3 py-2 text-sm font-semibold transition',
              view === v
                ? 'bg-gradient-to-br from-primary to-primary-pressed text-on-primary shadow-soft'
                : 'text-on-surface-variant hover:bg-surface-container',
            )}
          >
            {v === 'month' ? t('schedule.viewMonth') : v === 'week' ? t('schedule.viewWeek') : t('schedule.viewDay')}
          </button>
        ))}
      </div>

      {/* Period nav */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => shift(-1)}
          aria-label={t('schedule.viewMonth')}
          className="grid h-9 w-9 place-items-center rounded-button text-on-surface-variant transition hover:bg-surface-container-low"
        >
          <span className="material-symbols-outlined">chevron_left</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-center font-display font-bold capitalize text-on-surface">{periodLabel}</span>
          <button
            onClick={() => setCursor(startOfDay(new Date()))}
            className="rounded-pill bg-surface-container-low px-3 py-1 text-xs font-semibold text-on-surface-variant transition hover:bg-surface-container"
          >
            {t('schedule.today')}
          </button>
        </div>
        <button
          onClick={() => shift(1)}
          aria-label={t('schedule.viewMonth')}
          className="grid h-9 w-9 place-items-center rounded-button text-on-surface-variant transition hover:bg-surface-container-low"
        >
          <span className="material-symbols-outlined">chevron_right</span>
        </button>
      </div>

      <Legend showLive={showLive} showRooms={showRooms} />

      {/* ⚠️ Карточки «Live-уроки пока не открыты» с замком здесь больше НЕТ
          (решение владельца 14.09.2026, Apple 3.1.3(f)): никаких замков. Ученик без
          живых уроков просто видит остальные события расписания — как и любой пустой
          день. Не возвращать: «пока не открыты» читается как «откроются за деньги». */}

      {isError ? (
        <Card variant="subtle" className="text-error">
          {humanizeError(error)}
        </Card>
      ) : isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : view === 'month' ? (
        <MonthView cursor={cursor} items={items} onSelect={setSelected} />
      ) : view === 'week' ? (
        /* ⚠️ НАЖАТИЕ НА ДЕНЬ ВЫБИРАЕТ ЕГО, СПИСОК ДНЯ — ПОД СЕТКОЙ, как на компьютере (02.10.2026).
           До этого неделя рисовала одни точки, а нажатие уводило в «День»: владелец видел точку, не
           видел события и читал неделю как пустую (`docs/CHECK-2026-10-01-CALENDAR.md` §1).
           Выбранный день — это `cursor`: тот же день откроется и во вкладке «День». */
        <>
          <WeekView cursor={cursor} items={items} onPickDay={(d) => setCursor(startOfDay(d))} />
          <DayList day={cursor} items={itemsOn(items, cursor)} onSelect={setSelected} />
        </>
      ) : (
        <DayView cursor={cursor} items={items} onSelect={setSelected} />
      )}

      {selected && (
        <EventDetailsModal
          item={selected}
          showLive={showLive}
          showRooms={showRooms}
          onJoin={leaveForCall}
          onClose={() => setSelected(null)}
          onEdit={() => {
            if (selected.personal) {
              setEditing(selected.personal);
              setPersonalOpen(true);
              setSelected(null);
            }
          }}
          onDelete={() => selected.personal && deletePersonal.mutate(selected.personal.id)}
          deleting={deletePersonal.isPending}
          deleteError={deletePersonal.isError ? humanizeError(deletePersonal.error) : null}
        />
      )}

      {personalOpen && (
        <PersonalEventModal
          existing={editing}
          onClose={() => { setPersonalOpen(false); setEditing(null); }}
          onSaved={() => {
            void invalidateStudentCalendar(qc);
            setPersonalOpen(false);
            setEditing(null);
          }}
        />
      )}

      <AttendanceCodeModal open={codeOpen} onClose={() => setCodeOpen(false)} />
    </div>
  );
}

function Legend({ showLive, showRooms }: { showLive: boolean; showRooms: boolean }) {
  const { t } = useTranslation();
  const entries: { label: string; emoji: string }[] = [
    // Живые уроки и Speaking Rooms — в легенде, только когда они есть в самом календаре: строка о том,
    // чего ученик не увидит, обещала бы раздел, которого у него нет.
    ...(showLive ? [{ label: t('schedule.legendLiveLesson'), emoji: kindEmoji('live_lesson') }] : []),
    ...(showRooms ? [{ label: t('schedule.legendSpeakingRoom'), emoji: kindEmoji('speaking_room') }] : []),
    { label: t('schedule.legendPlatformEvent'), emoji: kindEmoji('webinar') },
    { label: t('schedule.legendPersonal'), emoji: kindEmoji('personal') },
  ];
  return (
    <div className="mt-3 flex flex-wrap gap-3 text-xs text-on-surface-variant">
      {entries.map((e) => (
        <span key={e.label} className="flex items-center gap-1.5">
          <span className="text-sm leading-none">{e.emoji}</span>
          {e.label}
        </span>
      ))}
    </div>
  );
}

function MonthView({ cursor, items, onSelect }: { cursor: Date; items: CalItem[]; onSelect: (i: CalItem) => void }) {
  const { t } = useTranslation();
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getTime() + i * MS_DAY));
  const today = new Date();
  const weekDays = [
    t('schedule.weekMon'),
    t('schedule.weekTue'),
    t('schedule.weekWed'),
    t('schedule.weekThu'),
    t('schedule.weekFri'),
    t('schedule.weekSat'),
    t('schedule.weekSun'),
  ];

  return (
    <Card>
      <div className="grid grid-cols-7 gap-px text-center text-xs font-semibold uppercase text-on-surface-variant">
        {weekDays.map((d) => (
          <div key={d} className="pb-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-button bg-surface-container-low">
        {days.map((d) => {
          const dayItems = items.filter((it) => sameDay(it.start, d));
          const inMonth = d.getMonth() === cursor.getMonth();
          return (
            <div
              key={d.toISOString()}
              className={`min-h-[5.5rem] bg-surface p-1.5 ${inMonth ? '' : 'opacity-40'}`}
            >
              <div className="flex justify-end">
                <span
                  className={cn(
                    'grid h-6 w-6 place-items-center rounded-full text-xs',
                    sameDay(d, today)
                      ? 'bg-gradient-to-br from-primary to-primary-pressed font-bold text-on-primary'
                      : 'text-on-surface-variant',
                  )}
                >
                  {d.getDate()}
                </span>
              </div>
              <div className="mt-1 space-y-1">
                {dayItems.slice(0, 3).map((it) => (
                  <button
                    key={it.id}
                    onClick={() => onSelect(it)}
                    className="flex w-full items-center gap-1 truncate rounded-button px-1 py-0.5 text-left text-[11px] text-on-surface hover:opacity-80"
                    style={{ backgroundColor: `${it.color}22` }}
                  >
                    <span className="shrink-0 text-[10px] leading-none">{kindEmoji(it.kind)}</span>
                    <span className="truncate">{it.title}</span>
                  </button>
                ))}
                {dayItems.length > 3 && (
                  <div className="px-1 text-[10px] text-on-surface-variant">+{dayItems.length - 3}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * Список событий одного дня под недельной сеткой — общий для компьютера и телефона.
 *
 * Пустой день — прямая строка, а не пустота: нажатие на день без событий обязано
 * что-то показать, иначе не понять, сломано это или так задумано.
 */
function DayList({ day, items, onSelect }: { day: Date; items: CalItem[]; onSelect: (i: CalItem) => void }) {
  const { t } = useTranslation();
  const now = new Date();
  return (
    <>
      <h2 className="mx-0.5 mb-[11px] mt-6 font-display text-[15.5px] font-bold text-on-surface">
        {day.toLocaleDateString(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' })}
      </h2>
      {items.length === 0 ? (
        <p className="rounded-[20px] bg-surface-container px-[18px] py-4 text-[13px] text-on-surface-variant">
          {t('schedule.nothingPlanned')}
        </p>
      ) : (
        <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
          {items.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => onSelect(e)}
              className="flex w-full items-center gap-3 border-b border-outline py-3 text-left last:border-b-0"
            >
              <span className="grid h-11 w-11 flex-none place-items-center rounded-[14px] bg-primary-container text-[11px] font-bold text-on-primary-container">
                {e.start.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-on-surface">{e.title}</span>
                <span className="block truncate text-[12.5px] text-on-surface-variant">
                  {kindEmoji(e.kind)}
                  {e.description ? ` · ${e.description}` : ''}
                </span>
              </span>
              {deadlineNear(e, now) && <DeadlineNearPill />}
              <span className="material-symbols-outlined flex-none text-[16px] text-on-surface-variant">chevron_right</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Метка события-дедлайна, до которого осталось не больше 48 часов (`deadlineNear`). */
function DeadlineNearPill() {
  const { t } = useTranslation();
  return (
    <span className="flex-none rounded-full bg-primary-container px-2 py-0.5 text-[10.5px] font-bold text-on-primary-container">
      {t('schedule.deadlineNear')}
    </span>
  );
}

/**
 * ⚠️ НАЖАТИЕ НА ДЕНЬ ВЫБИРАЕТ ДЕНЬ, А НЕ ОТКРЫВАЕТ ПЕРВОЕ СОБЫТИЕ. Первая редакция
 * (07.09.2026) открывала `dayItems[0]` — и на пустом дне не делала НИЧЕГО:
 * человек жмёт, экран не отвечает, и понять, сломано это или так задумано,
 * невозможно. Заказчик так это и поймал.
 *
 * Выбранный день подсвечен, его события — списком под сеткой (`DayList`).
 */
function WeekView({
  cursor,
  items,
  onPickDay,
}: {
  cursor: Date;
  items: CalItem[];
  onPickDay: (d: Date) => void;
}) {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * MS_DAY));
  const today = new Date();
  return (
    /* ⚠️ СЕМЬ ДНЕЙ В РЯД, КАК НА КОМПЬЮТЕРЕ. Раньше на телефоне неделя шла
       столбиком: семь карточек в высоту, и чтобы увидеть пятницу, надо было
       прокрутить весь экран. Неделю открывают ровно затем, чтобы увидеть её
       целиком с одного взгляда. */
    <div className="grid grid-cols-7 gap-1.5">
      {days.map((d) => {
        const dayItems = items.filter((it) => sameDay(it.start, d));
        const isToday = sameDay(d, today);
        // Подсвечен ВЫБРАННЫЙ день, сегодня — цветом числа: так же, как на компьютере.
        const isShown = sameDay(d, cursor);
        return (
          <button
            key={d.toISOString()}
            type="button"
            onClick={() => onPickDay(d)}
            aria-pressed={isShown}
            className={cn(
              'min-h-[104px] rounded-[16px] px-1.5 pb-2 pt-2 text-left transition',
              isShown ? 'bg-primary-container' : 'bg-surface-container',
            )}
          >
            <div
              className={cn(
                'text-[9px] font-semibold uppercase tracking-[0.04em]',
                isShown ? 'text-on-primary-container' : 'text-on-surface-variant',
              )}
            >
              {d.toLocaleDateString(dateLocale(), { weekday: 'short' }).replace('.', '')}
            </div>
            <div
              className={cn(
                'mt-0.5 text-[16px] font-bold tabular-nums',
                isToday ? 'text-primary' : isShown ? 'text-on-primary-container' : 'text-on-surface',
              )}
            >
              {d.getDate()}
            </div>
            {/* События помечаем точками: подписи в такую клетку не влезут, а
                «есть или нет» видно сразу. Сами события — списком под сеткой. */}
            <div className="mt-1.5 flex flex-wrap gap-1">
              {dayItems.slice(0, 4).map((it) => (
                <span key={it.id} className="h-1.5 w-1.5 rounded-full bg-primary" />
              ))}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DayView({ cursor, items, onSelect }: { cursor: Date; items: CalItem[]; onSelect: (i: CalItem) => void }) {
  const { t } = useTranslation();
  const dayItems = items.filter((it) => sameDay(it.start, cursor));
  return (
    <Card>
      {dayItems.length === 0 ? (
        <p className="text-center text-on-surface-variant">{t('schedule.nothingPlanned')}</p>
      ) : (
        <div className="space-y-2">
          {dayItems.map((it) => <ItemChip key={it.id} item={it} onSelect={onSelect} expanded />)}
        </div>
      )}
    </Card>
  );
}

function ItemChip({ item, onSelect, expanded }: { item: CalItem; onSelect: (i: CalItem) => void; expanded?: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={() => onSelect(item)}
      className="flex w-full items-start gap-2 rounded-button px-2 py-1.5 text-left hover:opacity-90"
      style={{ backgroundColor: `${item.color}1a`, borderLeft: `3px solid ${item.color}` }}
    >
      <div className="flex-1">
        <div className="text-sm font-medium text-on-surface">
          <span className="mr-1">{kindEmoji(item.kind)}</span>
          {item.title}
        </div>
        <div className="text-[11px] text-on-surface-variant">
          {item.start.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' })}
          {item.durationMin ? ` · ${t('schedule.durationMin', { min: item.durationMin })}` : ''}
          {deadlineNear(item, new Date()) ? ` · ${t('schedule.deadlineNear')}` : ''}
        </div>
        {expanded && item.description && (
          <div className="mt-0.5 text-xs text-on-surface">{item.description}</div>
        )}
      </div>
    </button>
  );
}

function EventDetailsModal({
  item,
  showLive,
  showRooms,
  onJoin,
  onClose,
  onEdit,
  onDelete,
  deleting,
  deleteError,
}: {
  item: CalItem;
  /** Живые уроки и комнаты доступны этому ученику — `lib/calendarAccess.ts`. */
  showLive: boolean;
  showRooms: boolean;
  /** Перед входом в комнату или на урок: страница запоминает день и неделю, чтобы вернуться на них. */
  onJoin: () => void;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
  deleteError?: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const kindLabel =
    item.kind === 'live_lesson' ? t('schedule.legendLiveLesson') :
    item.kind === 'speaking_room' ? t('schedule.legendSpeakingRoom') :
    t(EVENT_TYPE_KEYS[item.kind as CalendarEventType] ?? 'schedule.event');
  const isMeet = item.kind === 'live_lesson' || item.kind === 'speaking_room';
  // Внутренний адрес — только комната `/app/speaking/<id>`; наружу — только http(s). Всё остальное кнопки
  // не получает: нажатие в пустоту хуже отсутствия кнопки.
  const roomPath = item.kind === 'speaking_room' ? calendarInternalPath(item.link) : null;
  const outsideUrl = externalHttpUrl(item.link);
  // В приложении звонки только с микрофоном (решение владельца 02.10.2026) — значок камеры обманывал бы.
  const joinIcon = IS_APP_BUILD ? 'mic' : 'videocam';
  return (
    <Modal open onClose={onClose} title={item.title} size="sm">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Pill style={{ backgroundColor: `${item.color}22`, color: item.color }}>{kindLabel}</Pill>
          {deadlineNear(item, new Date()) && <DeadlineNearPill />}
        </div>
        <p className="text-sm text-on-surface-variant">
          {item.start.toLocaleString(dateLocale(), { dateStyle: 'full', timeStyle: 'short' })}
        </p>
        {item.description && <p className="text-sm text-on-surface">{item.description}</p>}
        {/* ⚠️ «ВОЙТИ» — ТОЛЬКО ПРИ РАЗДЕЛЕ (решение владельца 02.10.2026, одно правило на оба вида событий).
            Без раздела событие в список не попадает вовсе; условие стоит и здесь, чтобы кнопка не зависела
            от того, кто и как собрал список. Замков, выключенных кнопок и «скоро» нет. */}
        {showLive && item.lessonId && (
          <Button
            fullWidth
            onClick={() => {
              onJoin();
              navigate(`/app/live/${item.lessonId}`);
            }}
          >
            <span className="material-symbols-outlined">{joinIcon}</span>
            {t('schedule.joinRoom')}
          </Button>
        )}
        {/* ⚠️ ДВА РАЗНЫХ ПОДКЛЮЧЕНИЯ, И ИХ НЕЛЬЗЯ ПУТАТЬ.
            Разговорная комната и комната живого урока — НАШ экран внутри приложения: открыть их
            «наружу» значит вытолкнуть ученика в браузер, где он окажется без сессии и без микрофона.
            Ссылка Google Meet и ссылка события — чужой адрес, открывается наружу (ниже). */}
        {showRooms && roomPath && (
          <Button
            fullWidth
            onClick={() => {
              onJoin();
              navigate(roomPath);
            }}
          >
            <span className="material-symbols-outlined">{joinIcon}</span>
            {t('schedule.joinRoom')}
          </Button>
        )}
        {/* ⚠️ НАРУЖУ В ПРИЛОЖЕНИИ — КНОПКОЙ ЧЕРЕЗ `Browser.open`, А НЕ ССЫЛКОЙ `target="_blank"`
            (`lib/externalLink.ts`): у WKWebView нет вкладки, куда такой ссылке открыться. На сайте —
            обычная ссылка в новую вкладку, как было. */}
        {outsideUrl && (
          IS_APP_BUILD ? (
            <Button
              fullWidth
              variant={item.lessonId || roomPath ? 'ghost' : 'primary'}
              onClick={() => void openExternal(outsideUrl)}
            >
              <span className="material-symbols-outlined">launch</span>
              {isMeet ? t('schedule.joinMeet') : t('schedule.openLink')}
            </Button>
          ) : (
            <a href={outsideUrl} target="_blank" rel="noreferrer">
              <Button fullWidth variant={item.lessonId ? 'ghost' : 'primary'}>
                <span className="material-symbols-outlined">launch</span>
                {isMeet ? t('schedule.joinMeet') : t('schedule.openLink')}
              </Button>
            </a>
          )
        )}
        {item.personal && (
          confirmDelete ? (
            <div className="rounded-button bg-error/10 p-3">
              <p className="text-sm text-on-surface">{t('schedule.confirmDeleteEvent')}</p>
              {deleteError && <p className="mt-2 text-xs text-error">{deleteError}</p>}
              <div className="mt-3 flex gap-2">
                <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting} className="flex-1">
                  {t('common.cancel')}
                </Button>
                <Button variant="danger" onClick={onDelete} loading={deleting} className="flex-1">
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" onClick={onEdit} className="flex-1">{t('common.edit')}</Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="flex-1 text-error">
                {t('common.delete')}
              </Button>
            </div>
          )
        )}
      </div>
    </Modal>
  );
}

function toLocalInput(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
}

function PersonalEventModal({
  existing,
  onClose,
  onSaved,
}: {
  existing: CalendarEvent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: existing?.title ?? '',
    description: existing?.description ?? '',
    start_at: toLocalInput(existing?.start_at) || toLocalInput(new Date().toISOString()),
  });

  const save = useMutation({
    mutationFn: () => {
      const startIso = new Date(form.start_at).toISOString();
      const payload: Partial<CalendarEvent> = {
        title: form.title,
        description: form.description,
        start_at: startIso,
        end_at: startIso,
        type: 'personal',
      };
      return existing
        ? calendarApi.updatePersonal(existing.id, payload)
        : calendarApi.createPersonal(payload);
    },
    onSuccess: onSaved,
  });

  return (
    <Modal open onClose={onClose} title={existing ? t('schedule.editEvent') : t('schedule.personalEvent')} size="sm">
      <div className="space-y-3">
        <Input label={t('schedule.eventTitle')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Input label={t('schedule.eventNote')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Input type="datetime-local" label={t('schedule.eventWhen')} value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
        {save.isError && <p className="text-xs text-error">{humanizeError(save.error)}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.title || !form.start_at}>
            {existing ? t('common.save') : t('schedule.addEvent')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AttendanceCodeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await attendanceApi.submitCode(code);
      // Сервер отмечает всегда «present» (api/attendance.ts) — сырое значение ученику не показываем
      // (было «Готово — посещение отмечено (present).», промпт 12, 18.10.2026).
      setResult(t('schedule.attendanceSuccess'));
    } catch (e) {
      setError(humanizeError(e) || t('schedule.attendanceInvalidCode'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('schedule.attendanceCode')} size="sm">
      <p className="mb-4 text-sm text-on-surface-variant">
        {t('schedule.attendanceHint')}
      </p>
      <Input
        inputMode="numeric"
        maxLength={4}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder="0000"
        className="text-center text-2xl"
      />
      {error && <p className="mt-3 text-sm text-error">{error}</p>}
      {result && <p className="mt-3 text-sm text-success">{result}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('schedule.close')}
        </Button>
        <Button disabled={code.length !== 4 || submitting} loading={submitting} onClick={submit}>
          {t('schedule.markAttendance')}
        </Button>
      </div>
    </Modal>
  );
}
